/**
 * Routes. Search params are validated here so pages receive typed values;
 * unknown or malformed values fall back rather than throwing.
 */
import { createRootRoute, createRoute, createRouter, Outlet, redirect } from '@tanstack/react-router'
import { RegisterPage } from '../features/register/RegisterPage'
import { PropertyPage } from '../features/property/PropertyPage'
import { ListsPage } from '../features/lists/ListsPage'
import { SettingsPage, type SettingsSection } from '../features/settings/SettingsPage'
import { BillingInvoicePage, BillingPortalPage, BillingSettingsPage, PlansPage } from '../features/billing/BillingPages'
import { DealModelsPage } from '../features/deal-models/DealModelsPage'
import { DealModelWorkspace } from '../features/deal-models/DealModelWorkspace'
import { NotFoundPage } from '../features/stubs'

export const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/
const uuid = (value: unknown) => (typeof value === 'string' && UUID.test(value) ? value : undefined)
const text = (value: unknown, max = 200) => (typeof value === 'string' && value.trim() ? value.slice(0, max) : undefined)
const oneOf = <T extends string>(value: unknown, allowed: readonly T[]): T | undefined => (allowed as readonly string[]).includes(String(value)) ? (value as T) : undefined

export interface RegisterSearch { listId?: string }
export interface PropertySearch { listId?: string; dealModelId?: string; document?: string; page?: number }
export interface SettingsSearch { section?: SettingsSection }
export interface DealModelsSearch { q?: string; ownership?: 'all' | 'system' | 'mine'; status?: 'all' | 'ready' | 'inactive' | 'invalid' }
export interface DealModelEditorSearch { entity?: string; property?: string; scenario?: string }
export interface DealModelDataSearch { property?: string }
export interface DealModelFixturesSearch { fixture?: string }

const rootRoute = createRootRoute({ component: () => <Outlet />, notFoundComponent: NotFoundPage })

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  validateSearch: (search: Record<string, unknown>): RegisterSearch => ({ listId: uuid(search.listId) }),
  component: RegisterPage,
})

const propertyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/properties/$propertyId',
  validateSearch: (search: Record<string, unknown>): PropertySearch => ({
    listId: uuid(search.listId), dealModelId: uuid(search.dealModelId), document: uuid(search.document),
    page: typeof search.page === 'number' && Number.isInteger(search.page) && search.page > 0 ? search.page : undefined,
  }),
  component: PropertyPage,
})

const listsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/lists', component: ListsPage })

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  validateSearch: (search: Record<string, unknown>): SettingsSearch => ({ section: oneOf(search.section, ['appearance', 'refurbishment', 'homeBuyer', 'investor', 'billing'] as const) }),
  component: SettingsPage,
})
const billingSettingsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/settings/billing', component: BillingSettingsPage })
const plansRoute = createRoute({ getParentRoute: () => rootRoute, path: '/plans', component: PlansPage })
const billingPortalRoute = createRoute({ getParentRoute: () => rootRoute, path: '/billing/portal', component: BillingPortalPage })
const billingInvoiceRoute = createRoute({ getParentRoute: () => rootRoute, path: '/billing/invoices/$invoiceId', component: BillingInvoicePage })

const dealModelsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/deal-models',
  validateSearch: (search: Record<string, unknown>): DealModelsSearch => ({ q: text(search.q), ownership: oneOf(search.ownership, ['all', 'system', 'mine'] as const), status: oneOf(search.status, ['all', 'ready', 'inactive', 'invalid'] as const) }),
  component: DealModelsPage,
})
const dealModelRootRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/deal-models/$modelId',
  beforeLoad: ({ params }) => { throw redirect({ to: '/deal-models/$modelId/editor', params, search: {} }) },
})
const dealModelEditorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/deal-models/$modelId/editor',
  validateSearch: (search: Record<string, unknown>): DealModelEditorSearch => ({ entity: uuid(search.entity), property: uuid(search.property), scenario: text(search.scenario, 120) }),
  component: () => <DealModelWorkspace area="editor" />,
})
const dealModelDataRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/deal-models/$modelId/data',
  validateSearch: (search: Record<string, unknown>): DealModelDataSearch => ({ property: uuid(search.property) }),
  component: () => <DealModelWorkspace area="data" />,
})
const dealModelFixturesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/deal-models/$modelId/fixtures',
  validateSearch: (search: Record<string, unknown>): DealModelFixturesSearch => ({ fixture: uuid(search.fixture) }),
  component: () => <DealModelWorkspace area="fixtures" />,
})

export const router = createRouter({
  routeTree: rootRoute.addChildren([
    registerRoute, propertyRoute, listsRoute,
    settingsRoute, billingSettingsRoute, plansRoute, billingPortalRoute, billingInvoiceRoute,
    dealModelsRoute, dealModelRootRoute, dealModelEditorRoute, dealModelDataRoute, dealModelFixturesRoute,
  ]),
})

declare module '@tanstack/react-router' {
  interface Register { router: typeof router }
}
